/**
 * Custom Scrollbar Module
 *
 * Creates a custom scrollbar that overlays the content.
 *
 * @package TheRealEstates
 */

export default class CustomScrollbar {
  constructor(lenis = null) {
    this.lenis = lenis;
    this.scrollbar = null;
    this.thumb = null;
    this.isDragging = false;
    this.startY = 0;
    this.startScroll = 0;

    this.init();
  }

  init() {
    this.createScrollbar();
    this.bindEvents();
    this.update();
  }

  createScrollbar() {
    // Create scrollbar track
    this.scrollbar = document.createElement('div');
    this.scrollbar.className = 'custom-scrollbar';

    // Create thumb
    this.thumb = document.createElement('div');
    this.thumb.className = 'custom-scrollbar__thumb';

    this.scrollbar.appendChild(this.thumb);
    document.body.appendChild(this.scrollbar);
  }

  bindEvents() {
    // Update on scroll
    if (this.lenis) {
      this.lenis.on('scroll', () => this.update());
    } else {
      window.addEventListener('scroll', () => this.update(), { passive: true });
    }

    // Update on resize
    window.addEventListener('resize', () => this.update(), { passive: true });

    // Drag functionality
    this.thumb.addEventListener('mousedown', (e) => this.onDragStart(e));
    document.addEventListener('mousemove', (e) => this.onDragMove(e));
    document.addEventListener('mouseup', () => this.onDragEnd());

    // Click on track to jump
    this.scrollbar.addEventListener('click', (e) => this.onTrackClick(e));

    // Show/hide on hover
    this.scrollbar.addEventListener('mouseenter', () => this.show());
    this.scrollbar.addEventListener('mouseleave', () => {
      if (!this.isDragging) this.hide();
    });

    // Show on scroll, hide after delay
    let hideTimeout;
    const showOnScroll = () => {
      this.show();
      clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        if (!this.isDragging) this.hide();
      }, 1500);
    };

    if (this.lenis) {
      this.lenis.on('scroll', showOnScroll);
    } else {
      window.addEventListener('scroll', showOnScroll, { passive: true });
    }
  }

  update() {
    const scrollHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrollTop = this.lenis ? this.lenis.scroll : window.scrollY;

    // Calculate thumb size (proportional to viewport/content ratio)
    const thumbHeight = Math.max((viewportHeight / scrollHeight) * viewportHeight, 40);

    // Calculate thumb position
    const maxScroll = scrollHeight - viewportHeight;
    const scrollProgress = maxScroll > 0 ? scrollTop / maxScroll : 0;
    const maxThumbTop = viewportHeight - thumbHeight;
    const thumbTop = scrollProgress * maxThumbTop;

    this.thumb.style.height = `${thumbHeight}px`;
    this.thumb.style.transform = `translateY(${thumbTop}px)`;
  }

  onDragStart(e) {
    e.preventDefault();
    this.isDragging = true;
    this.startY = e.clientY;
    this.startScroll = this.lenis ? this.lenis.scroll : window.scrollY;
    this.scrollbar.classList.add('is-dragging');
    document.body.style.userSelect = 'none';
  }

  onDragMove(e) {
    if (!this.isDragging) return;

    const deltaY = e.clientY - this.startY;
    const scrollHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    const thumbHeight = parseFloat(this.thumb.style.height);

    // Convert thumb movement to scroll movement
    const scrollRatio = (scrollHeight - viewportHeight) / (viewportHeight - thumbHeight);
    const newScroll = this.startScroll + (deltaY * scrollRatio);

    if (this.lenis) {
      this.lenis.scrollTo(newScroll, { immediate: true });
    } else {
      window.scrollTo(0, newScroll);
    }
  }

  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.scrollbar.classList.remove('is-dragging');
    document.body.style.userSelect = '';
  }

  onTrackClick(e) {
    if (e.target === this.thumb) return;

    const rect = this.scrollbar.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const viewportHeight = window.innerHeight;
    const scrollHeight = document.documentElement.scrollHeight;

    const scrollProgress = clickY / viewportHeight;
    const targetScroll = scrollProgress * (scrollHeight - viewportHeight);

    if (this.lenis) {
      this.lenis.scrollTo(targetScroll, { duration: 0.5 });
    } else {
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }
  }

  show() {
    this.scrollbar.classList.add('is-visible');
  }

  hide() {
    this.scrollbar.classList.remove('is-visible');
  }

  destroy() {
    if (this.scrollbar && this.scrollbar.parentNode) {
      this.scrollbar.parentNode.removeChild(this.scrollbar);
    }
  }
}
